import { useMemo } from 'react';
import { useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments } from '@zg-rentals/ts-capps-client-react-query';
import type { IncomeDocumentWebApiDto } from '@zg-rentals/ts-capps-client';
import type { Document } from '../models/rental-application';

// redactedDocumentId is populated once the redaction check has finished; a failed check leaves it
// empty but marks the document as not redacted, which is also a finished check
export function isRedactionCheckComplete(doc: IncomeDocumentWebApiDto) {
  return !!doc.redactedDocumentId || doc.isRedacted === false;
}

export function toDocument(doc: IncomeDocumentWebApiDto): Document {
  return {
    id: doc.documentId,
    fileName: doc.fileName,
    // A redacted document falls back to the unredacted preview when the redacted one is missing,
    // matching the retrieveSharedDocuments behavior the old graphql layer relied on
    previewUrl: doc.isRedacted ? (doc.redactedPreviewUrl ?? doc.previewUrl) : doc.previewUrl,
    isRedacted: doc.isRedacted,
    expirationDateTime: doc.expirationDateUtc ? new Date(doc.expirationDateUtc).toISOString() : null,
    // Until the check has finished we can't tell whether the document can be redacted
    canBeRedacted: isRedactionCheckComplete(doc) ? doc.redactionTags.length > 0 : null,
    isRedactionCheckComplete: isRedactionCheckComplete(doc),
  };
}

/**
 * Loads the current document info for the given income documents, so callers can render from the
 * documents api even when the application answers only carry document ids
 */
export function useRetrieveActiveDocuments(documents: Array<Document>) {
  const documentIds = documents.map((doc) => doc.id).filter((id): id is string => !!id);
  const enabled = documentIds.length > 0;

  const { data, isPending } = useCappsRenterDocumentsWebApiHandler_retrieveActiveDocuments(
    { documentIds },
    {
      enabled,
      meta: {
        tags: ['incomeDocuments'],
        context: { api: 'cappsRenterDocuments', details: 'retrieveActiveDocuments' },
      },
    },
  );

  const activeDocuments = useMemo(() => data?.documentInfoList.map(toDocument), [data]);

  // A disabled query stays pending forever, so only report pending when it can actually run
  return { documents: activeDocuments, isPending: enabled && isPending };
}
